import React, { useContext } from 'react';
import Icon from '@common/assets';
import Text from '@common/text';
import Portal from '@common/portal';
import { useChatToast } from '@components/chat/common/chatToast';
import useChatNotification from '@components/chat/hooks/useChatNotification';
import { ChatContext, ChatContextType } from '@components/chat/ChatContext';
import Colors from '@domain/constant/colors';
import { FontStyle } from '@domain/constant/fontStyle';
import { ChatMenu } from '@domain/constant/ChatMenuType';
import useClickAway from '@utils/hooks/useClickOutside';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

interface ChatListItemMenuProps {
  position: { top: number; left: number } | undefined;
}

const ChatListItemMenu = ({ position }: ChatListItemMenuProps) => {
  const {
    channelsData,
    myChannelsData,
    leaveChannel,
    setChatMenu,
    isNotifying,
    selectedChannel,
    setSelectedChannel,
    fetchMyChannels,
  } = useContext<ChatContextType>(ChatContext);

  const ref = React.useRef<HTMLDivElement | null>(null);
  const { onNotification, offNotification } = useChatNotification();
  const ChatToast = useChatToast();

  const handleClose = () => setSelectedChannel(null);

  useClickAway(handleClose, ref);

  const handleClickOn = async () => {
    if (!selectedChannel) return;
    await onNotification({
      channelId: selectedChannel.id,
      callback: () => {
        handleClose();
        ChatToast({
          content: (
            <div>
              <Text element={'span'}>알림이 켜졌어요.</Text>
              <br />
              <Text element={'span'} fontStyle={FontStyle.B4_M}>
                이제 모바일 앱을 통해 새 메시지, 공고를 실시간으로 받아보세요.
              </Text>
            </div>
          ),
        });
      },
    });
  };

  const handleClickOff = async () => {
    if (!selectedChannel) return;
    await offNotification({
      channelId: selectedChannel.id,
      callback: () => {
        handleClose();
        ChatToast({
          content: (
            <div>
              <Text element={'span'}>알림이 꺼졌어요.</Text>
              <br />
              <Text element={'span'} fontStyle={FontStyle.B4_M}>
                새 메시지, 공고를 쉽게 확인하고 싶다면 알림을 켜주세요.
              </Text>
            </div>
          ),
        });
      },
    });
  };

  const handleLeaveChannel = async () => {
    if (!selectedChannel) return;
    const findChannel = channelsData?.channels?.find((channel) => channel.id === selectedChannel.id);
    if (!findChannel) return;
    const isJoin = !!myChannelsData?.channels?.find((channel) => channel.id === selectedChannel.id);
    findChannel.memberCount = isJoin ? (selectedChannel?.memberCount || 0) - 1 : selectedChannel?.memberCount || 0;
    await leaveChannel(selectedChannel);
    fetchMyChannels();
    setChatMenu(ChatMenu.LIST);
    handleClose();
  };

  if (!selectedChannel) return null;

  return (
    <Portal selector="#chat">
      <div ref={ref} className={cx('menuArea')} style={{ top: position?.top, left: position?.left }}>
        {isNotifying ? (
          <button className={cx('btn')} onClick={handleClickOff}>
            <Icon name={'bellOffLight'} width={24} height={24} fill={Colors.C_COOL_GRAY_80} />
            <Text className={cx('text')}>알림 끄기</Text>
          </button>
        ) : (
          <button className={cx('btn')} onClick={handleClickOn}>
            <Icon name={'bellLight'} width={24} height={24} fill={Colors.C_COOL_GRAY_80} />
            <Text className={cx('text')}>알림 켜기</Text>
          </button>
        )}
        <button className={cx('btn')} onClick={handleLeaveChannel}>
          <Icon name={'outputLight'} width={24} height={24} fill={Colors.C_COOL_GRAY_80} />
          <Text className={cx('text')}>채팅방 나가기</Text>
        </button>
      </div>
    </Portal>
  );
};

export default ChatListItemMenu;
